'use client';

import '@/lib/view-as/fetch-backstop';
import AuthService from '@/@auth/services';
import { ViewAsRestoring } from '@/@subsidiary/components/ViewAsRestoring';
import { SubsidiaryFilterContext } from '@/@subsidiary/context';
import { fetchSubsidiaryScopeForOrg } from '@/@subsidiary/services';
import type { SubsidiaryFilterContextValue, SubsidiaryOption } from '@/@subsidiary/types';
import {
    checkViewAs,
    endViewAs,
    ensureViewAsFresh,
    resumeViewAs,
    startViewAs as startViewAsSession,
    stopViewAs as stopViewAsSession,
} from '@/@subsidiary/view-as-lifecycle';
import {
    clearViewAsEntry,
    readViewAsEntry,
    takeViewAsEndReason,
} from '@/@subsidiary/view-as-storage';
import { getViewAsSnapshot, IDLE_VIEW_AS, subscribeViewAs } from '@/@subsidiary/view-as-store';
import { userPreferenceService } from '@/lib/services/userPreferences';
import { VIEW_AS_RECHECK_INTERVAL_MS } from '@/lib/view-as/constants';
import { useSession } from '@/providers/enhanced-session';
import { useDisclosure } from '@heroui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
    type PropsWithChildren,
} from 'react';
import { toast } from 'react-toastify';
import { useDebounceCallback } from 'usehooks-ts';

const EMPTY_SF_ACCOUNT_IDS: string[] = [];

export function SubsidiaryFilterProvider({ children }: PropsWithChildren) {
    const { data: session } = useSession();
    const hasSubsidiaryAccess = session?.user?.hasSubsidiaryAccess === true;
    /**
     * Staff View-as (search any client) only when the user is internal *and* not
     * themselves a hierarchy member. @mfour.com test accounts in a mapped client
     * org should get the bounded org switcher, not the internal masquerade UI.
     */
    const isInternalMasquerade = session?.user?.isInternal === true && !hasSubsidiaryAccess;
    const isVisible = hasSubsidiaryAccess || session?.user?.isInternal === true;
    /** The tab storage key's owner. Only a staff member's own entry is ever resumed. */
    const staffUserId = session?.user?.id ?? null;

    /**
     * Staff View-as (M4S-5670 §4). The module store is the source of truth for
     * the backstops and the DANI send guard; React reads it through this hook.
     */
    const viewAs = useSyncExternalStore(subscribeViewAs, getViewAsSnapshot, () => IDLE_VIEW_AS);
    /**
     * Whether the app may render yet. A tab that holds a stored session shows
     * "Restoring View as…" until the server has verified it, so nothing
     * renders, queries or writes as the staff member in a tab that is meant to
     * be the client's.
     *
     * The stored session lives in sessionStorage, which the server render
     * cannot see, so for staff the server and the first browser render both
     * show a plain spinner (`pending`) and the mount effect decides. Clients
     * never hold a View-as session and are never delayed.
     */
    const [gate, setGate] = useState<'pending' | 'restoring' | 'ready'>(() =>
        session?.user?.isInternal === true && !hasSubsidiaryAccess ? 'pending' : 'ready',
    );
    const resumeStarted = useRef(false);

    /** External hierarchy filter, or internal effective API filter org IDs. */
    const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
    /** Internal View-as entry org (the client picked via search). */
    const [masqueradeOrgId, setMasqueradeOrgId] = useState<number | null>(null);
    /**
     * SF account id for the View-as entry org from search seed.
     * Kept separately so list filters still resolve if scope options omit sfAccountId
     * or temporarily don't include the picked org.
     */
    const [masqueradeSfAccountId, setMasqueradeSfAccountId] = useState<string | null>(null);
    /** Sibling/parent options when masquerading into a hierarchy. */
    const [hierarchyOptions, setHierarchyOptions] = useState<SubsidiaryOption[]>([]);
    /** Display name for the hierarchy group (parent org when available). */
    const [hierarchyGroupName, setHierarchyGroupName] = useState<string | null>(null);
    /**
     * Within-hierarchy selection for internals:
     * null = View all (emit all hierarchy org IDs)
     * number[] = one or more orgs within the group
     */
    const [hierarchyFilterOrgIds, setHierarchyFilterOrgIds] = useState<number[] | null>(null);
    const [hierarchyLoading, setHierarchyLoading] = useState(false);
    const [orgSwitchEpoch, setOrgSwitchEpoch] = useState(0);
    const hydratedPref = useRef(false);
    const {
        isOpen: isOrgDrawerOpen,
        onOpen: openOrgDrawer,
        onClose: closeOrgDrawer,
    } = useDisclosure();

    const notifyOrgSwitch = useCallback(() => {
        setOrgSwitchEpoch((n) => n + 1);
    }, []);

    // Hierarchy users: load bounded subsidiary options once.
    // Internal View-as: client search is live against /organization/list (see
    // InternalViewAsSearchControl) — do not preload the full dropdown.
    const optionsQuery = useQuery({
        queryKey: ['subsidiaryOptions', isInternalMasquerade],
        queryFn: async (): Promise<SubsidiaryOption[]> => AuthService.getSubsidiaryOptions(),
        enabled: isVisible && !!session?.user?.id && !isInternalMasquerade,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });

    useEffect(() => {
        if (!optionsQuery.error) return;
        toast.error('Could not load clients for View as / filter. Try refreshing.', {
            position: 'top-right',
            autoClose: 5000,
        });
    }, [optionsQuery.error]);

    const preferenceQuery = useQuery({
        queryKey: ['subsidiaryFilterPref'],
        queryFn: async () => {
            try {
                const { data } = await userPreferenceService.getSubsidiaryFilter();
                return data;
            } catch (error) {
                // Older APIs / missing org_user used to 404 — treat as unsaved empty filter.
                const status = (error as { response?: { status?: number } })?.response?.status;
                if (status === 404) {
                    return {
                        orgIds: [] as number[],
                        savedOrgIds: [] as number[],
                        cleanedDuringRead: false,
                    };
                }
                throw error;
            }
        },
        enabled: isVisible && !isInternalMasquerade && !!session?.user?.id,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const persistMutation = useMutation({
        mutationFn: (orgIds: number[]) => userPreferenceService.setSubsidiaryFilter(orgIds),
    });

    const debouncedPersist = useDebounceCallback((orgIds: number[]) => {
        if (isInternalMasquerade) return;
        persistMutation.mutate(orgIds);
    }, 500);

    useEffect(() => {
        if (isInternalMasquerade || hydratedPref.current || !preferenceQuery.data) return;
        hydratedPref.current = true;
        const { orgIds, cleanedDuringRead, savedOrgIds } = preferenceQuery.data;
        setSelectedOrgIds(orgIds ?? []);

        if (cleanedDuringRead) {
            void userPreferenceService.setSubsidiaryFilter(orgIds ?? []);
            if ((savedOrgIds?.length ?? 0) > 0 && (orgIds?.length ?? 0) === 0) {
                toast.info(
                    'Your saved filter referenced clients you no longer have access to and has been reset.',
                    { position: 'top-right', autoClose: 5000 },
                );
            }
        }
    }, [isInternalMasquerade, preferenceQuery.data]);

    const options = useMemo(() => optionsQuery.data ?? [], [optionsQuery.data]);
    const hasHierarchySwitcher = hierarchyOptions.length > 1;

    /** Avoid new array identity when contents are unchanged (prevents consumer thrash). */
    const replaceSelectedOrgIds = useCallback((next: number[]) => {
        setSelectedOrgIds((prev) => {
            if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
                return prev;
            }
            return next;
        });
    }, []);

    // Keep selectedOrgIds in sync for internal hierarchy View-all / multi filter
    useEffect(() => {
        if (!isInternalMasquerade || masqueradeOrgId == null) return;
        if (!hasHierarchySwitcher) {
            replaceSelectedOrgIds([masqueradeOrgId]);
            return;
        }
        if (hierarchyFilterOrgIds == null || hierarchyFilterOrgIds.length === 0) {
            replaceSelectedOrgIds(hierarchyOptions.map((o) => o.orgId));
        } else {
            replaceSelectedOrgIds(hierarchyFilterOrgIds);
        }
    }, [
        isInternalMasquerade,
        masqueradeOrgId,
        hasHierarchySwitcher,
        hierarchyFilterOrgIds,
        hierarchyOptions,
        replaceSelectedOrgIds,
    ]);
    /** Keep array identity when SF ids are unchanged (avoids layout/DANI consumer thrash). */
    const selectedSfAccountIdsRef = useRef<string[]>(EMPTY_SF_ACCOUNT_IDS);
    const selectedSfAccountIds = useMemo(() => {
        const byId = new Map(
            [...options, ...hierarchyOptions].map((o) => [o.orgId, o.sfAccountId]),
        );
        if (masqueradeOrgId != null && masqueradeSfAccountId) {
            byId.set(masqueradeOrgId, masqueradeSfAccountId);
        }
        // Hierarchy "View all" keeps selectedOrgIds empty (full home scope). Expand to
        // every option's SF account so GraphQL/list filters are scoped, not unscoped.
        const orgIdsForFilter =
            selectedOrgIds.length > 0
                ? selectedOrgIds
                : !isInternalMasquerade && options.length > 0
                  ? options.map((o) => o.orgId)
                  : [];
        let next = orgIdsForFilter
            .map((id) => byId.get(id))
            .filter((id): id is string => Boolean(id));
        // Narrowed View-as with a known seed SF id — never return empty (that disables
        // My Surveys forever behind the loading gate).
        if (
            next.length === 0 &&
            isInternalMasquerade &&
            masqueradeSfAccountId &&
            selectedOrgIds.length <= 1
        ) {
            next = [masqueradeSfAccountId];
        }
        if (next.length === 0) {
            selectedSfAccountIdsRef.current = EMPTY_SF_ACCOUNT_IDS;
            return EMPTY_SF_ACCOUNT_IDS;
        }
        const prev = selectedSfAccountIdsRef.current;
        if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
            return prev;
        }
        selectedSfAccountIdsRef.current = next;
        return next;
    }, [
        options,
        hierarchyOptions,
        selectedOrgIds,
        isInternalMasquerade,
        masqueradeOrgId,
        masqueradeSfAccountId,
    ]);

    const applyInternalMasquerade = useCallback(
        async (orgId: number | null, seed?: SubsidiaryOption) => {
            if (orgId == null) {
                setMasqueradeOrgId(null);
                setMasqueradeSfAccountId(null);
                setHierarchyOptions([]);
                setHierarchyGroupName(null);
                setHierarchyFilterOrgIds(null);
                replaceSelectedOrgIds([]);
                return;
            }

            const seedSf = seed?.orgId === orgId && seed.sfAccountId ? seed.sfAccountId : null;
            setMasqueradeOrgId(orgId);
            if (seedSf) {
                setMasqueradeSfAccountId(seedSf);
            }
            // Seed immediately so list filters resolve sfAccountId before scope loads
            if (seed && seed.orgId === orgId && seed.sfAccountId) {
                setHierarchyOptions([seed]);
            }
            replaceSelectedOrgIds([orgId]);
            setHierarchyLoading(true);
            try {
                const scope = await fetchSubsidiaryScopeForOrg(orgId);
                const scopeOptions = scope.options ?? [];
                // Preserve seed/prior sfAccountId when scope rows omit it.
                const mergeSf = (o: SubsidiaryOption): SubsidiaryOption => {
                    if (o.sfAccountId) return o;
                    if (seed?.orgId === o.orgId && seed.sfAccountId) {
                        return { ...o, sfAccountId: seed.sfAccountId };
                    }
                    return o;
                };
                let nextOptions =
                    scopeOptions.length > 0
                        ? scopeOptions.map(mergeSf)
                        : seed && seed.orgId === orgId
                          ? [seed]
                          : [];
                // Always keep the picked org in the option list (scope may omit it).
                if (seed && seed.orgId === orgId && !nextOptions.some((o) => o.orgId === orgId)) {
                    nextOptions = [seed, ...nextOptions];
                }
                const resolvedSf =
                    nextOptions.find((o) => o.orgId === orgId)?.sfAccountId || seedSf || null;
                if (resolvedSf) {
                    setMasqueradeSfAccountId(resolvedSf);
                }
                setHierarchyOptions(nextOptions);
                if (nextOptions.length > 1) {
                    const parentName =
                        (scope.parentOrgId != null
                            ? nextOptions.find((o) => o.orgId === scope.parentOrgId)?.orgName
                            : undefined) ??
                        nextOptions.find((o) => o.orgId === orgId)?.orgName ??
                        'client group';
                    setHierarchyGroupName(parentName);
                    // Start on the exact org they picked; drawer can expand to View all
                    setHierarchyFilterOrgIds([orgId]);
                    replaceSelectedOrgIds([orgId]);
                } else {
                    setHierarchyGroupName(null);
                    setHierarchyFilterOrgIds(null);
                    replaceSelectedOrgIds([orgId]);
                }
            } catch {
                toast.error('Could not load organization group for View as.', {
                    position: 'top-right',
                    autoClose: 5000,
                });
                if (seed && seed.orgId === orgId) {
                    setHierarchyOptions([seed]);
                    if (seed.sfAccountId) setMasqueradeSfAccountId(seed.sfAccountId);
                } else {
                    setHierarchyOptions([]);
                }
                setHierarchyGroupName(null);
                setHierarchyFilterOrgIds(null);
                replaceSelectedOrgIds([orgId]);
            } finally {
                setHierarchyLoading(false);
            }
        },
        [replaceSelectedOrgIds],
    );

    // Show the one-shot "View as ended" notice left by the end path's reload,
    // then resume a stored session through the server before anything renders.
    useEffect(() => {
        if (!staffUserId || resumeStarted.current) return;
        resumeStarted.current = true;

        const endReason = takeViewAsEndReason();
        if (endReason) {
            toast.error(`View as ended: ${endReason}`, { position: 'top-right', autoClose: 8000 });
        }

        if (!isInternalMasquerade) {
            clearViewAsEntry();
            setGate('ready');
            return;
        }
        if (!readViewAsEntry(staffUserId)) {
            setGate('ready');
            return;
        }
        setGate('restoring');
        void resumeViewAs(staffUserId).then(async (verified) => {
            if (verified) {
                await applyInternalMasquerade(verified.client.orgId, {
                    orgId: verified.client.orgId,
                    sfAccountId: verified.client.sfAccountId,
                    orgName: verified.client.orgName,
                });
            }
            setGate('ready');
        });
    }, [staffUserId, isInternalMasquerade, applyInternalMasquerade]);

    // While active: re-check on becoming visible and every 5 minutes while
    // visible, and end at the 12h expiry.
    useEffect(() => {
        if (viewAs.status !== 'active' || !viewAs.sessionId) return;
        const recheckIfVisible = () => {
            if (document.visibilityState === 'visible') void checkViewAs();
        };
        document.addEventListener('visibilitychange', recheckIfVisible);
        const interval = setInterval(recheckIfVisible, VIEW_AS_RECHECK_INTERVAL_MS);
        const expiry =
            viewAs.expiresAt != null
                ? setTimeout(
                      () => endViewAs('the session reached its 12-hour limit.'),
                      Math.max(0, viewAs.expiresAt - Date.now()),
                  )
                : undefined;
        return () => {
            document.removeEventListener('visibilitychange', recheckIfVisible);
            clearInterval(interval);
            if (expiry) clearTimeout(expiry);
        };
    }, [viewAs.status, viewAs.sessionId, viewAs.expiresAt]);

    const startViewAs = useCallback(
        async (client: { sfAccountId: string; orgId?: number }) => {
            if (!staffUserId || !isInternalMasquerade) return false;
            return startViewAsSession(staffUserId, client);
        },
        [isInternalMasquerade, staffUserId],
    );

    const stopViewAs = useCallback((reason?: string) => stopViewAsSession(reason), []);

    const setSelected = useCallback(
        (orgIds: number[], seed?: SubsidiaryOption) => {
            if (isInternalMasquerade) {
                // Staff View-as always goes through a recorded session and a reload.
                const next = orgIds[0] ?? null;
                if (next == null) {
                    void stopViewAs('clear');
                    return;
                }
                if (!seed?.sfAccountId) {
                    toast.error('Could not start View as: that client has no Salesforce id.');
                    return;
                }
                void startViewAs({ sfAccountId: seed.sfAccountId, orgId: next });
                return;
            }
            setSelectedOrgIds(orgIds);
            debouncedPersist(orgIds);
        },
        [debouncedPersist, isInternalMasquerade, startViewAs, stopViewAs],
    );

    const setHierarchyFilter = useCallback(
        (orgIds: number[]) => {
            // Allow updates whenever View-as is active — hasHierarchySwitcher can be
            // false briefly while scope is still loading from a single seed option.
            if (!isInternalMasquerade || masqueradeOrgId == null) return;
            if (!orgIds.length) {
                setHierarchyFilterOrgIds(null);
                return;
            }
            setHierarchyFilterOrgIds(orgIds);
        },
        [isInternalMasquerade, masqueradeOrgId],
    );

    const value = useMemo<SubsidiaryFilterContextValue>(
        () => ({
            isVisible,
            isInternal: isInternalMasquerade,
            options,
            hierarchyOptions,
            hasHierarchySwitcher,
            hierarchyGroupName,
            selectedOrgIds,
            selectedSfAccountIds,
            setSelected,
            setHierarchyFilter,
            masqueradeOrgId,
            isLoading:
                hierarchyLoading ||
                (!isInternalMasquerade && (optionsQuery.isLoading || preferenceQuery.isLoading)),
            orgSwitchEpoch,
            notifyOrgSwitch,
            viewAsSessionId: viewAs.sessionId,
            viewAsStatus: viewAs.status,
            viewAsOrgName: viewAs.status === 'active' ? viewAs.orgName : null,
            startViewAs,
            stopViewAs,
            ensureViewAsFresh,
            isOrgDrawerOpen,
            openOrgDrawer,
            closeOrgDrawer,
        }),
        [
            viewAs.sessionId,
            viewAs.status,
            viewAs.orgName,
            startViewAs,
            stopViewAs,
            isVisible,
            isInternalMasquerade,
            options,
            hierarchyOptions,
            hasHierarchySwitcher,
            hierarchyGroupName,
            selectedOrgIds,
            selectedSfAccountIds,
            setSelected,
            setHierarchyFilter,
            masqueradeOrgId,
            optionsQuery.isLoading,
            hierarchyLoading,
            preferenceQuery.isLoading,
            orgSwitchEpoch,
            notifyOrgSwitch,
            isOrgDrawerOpen,
            openOrgDrawer,
            closeOrgDrawer,
        ],
    );

    return (
        <SubsidiaryFilterContext.Provider value={value}>
            {gate === 'ready' ? children : <ViewAsRestoring quiet={gate === 'pending'} />}
        </SubsidiaryFilterContext.Provider>
    );
}
